import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  completeMultipartUpload,
  initMultipartUpload,
  REDUX_ACTION,
  setReduxAction,
  SilentThunkError,
  uploadChunk,
} from '@socialive/common';
import { nanoid } from 'nanoid';
import { selectUploads } from '~/selectors';
import { totalProgress } from '~/slice';

const CHUNK_SIZE = 1024 * 1024 * 10;

const multipartUpload = createAsyncThunk(
  'uploads/multipartUpload',
  async (
    { id = nanoid(), file, bucket, optionalData, fileNameOverride, headers, chunkSize = CHUNK_SIZE, trigger },
    { dispatch, getState }
  ) => {
    if (!(file instanceof Blob)) throw new SilentThunkError('`file` must be a blob');

    const partETags = [];
    const { uploadId, key } = await dispatch(
      initMultipartUpload({
        id,
        bucket,
        originalname: fileNameOverride ?? file.name,
        optionalData,
        headers,
        trigger,
      })
    ).unwrap();

    let start = 0;
    let partNumber = 0;
    while (start < file.size) {
      const end = Math.min(start + chunkSize, file.size);
      const blob = file.slice(start, end);
      start = end;
      partNumber += 1;
      // eslint-disable-next-line no-await-in-loop
      const { tagId } = await dispatch(
        uploadChunk({
          bucket,
          contentType: file.type,
          id,
          blob,
          partNumber,
          uploadId,
          filename: key,
          trigger,
        })
      ).unwrap();

      const { canceling } = selectUploads(getState())[id] ?? {};

      if (canceling) {
        throw new SilentThunkError(`upload ${id} canceled`);
      }

      // eslint-disable-next-line no-await-in-loop
      await dispatch(
        totalProgress({
          id,
          completedChunk: blob.size,
          trigger,
        })
      );

      partETags.push({ partNumber, tagId });
    }

    await dispatch(
      completeMultipartUpload({
        bucket,
        key,
        uploadId,
        partETags,
        trigger,
      })
    ).unwrap();

    return { id, name: file.name };
  }
);

setReduxAction(REDUX_ACTION.MULTIPART_UPLOAD, multipartUpload);

export default multipartUpload;
